import React, { useCallback } from 'react'
import { useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import {
  DEFAULT_TOP_BAR_SUBTITLE_STYLES,
  DEFAULT_TOP_BAR_TITLE_STYLES,
  ITextStyles,
  ITopBarComponentInstance,
  TextAlignment,
} from 'src/types/report-designer'
import { AppTextField } from '../../AppTextField'
import { getBackgroundColors } from './PropertyConfigurationSection.action'
import {
  AlignmentToggleGroup,
  BoldToggleRow,
  ColorSwatchField,
  FontSizeSelect,
  FontFamilySelect,
  LineHeightSelect,
  LetterSpacingSelect,
} from './PropertyComponents'
import {
  FieldSection,
  FONT_SIZES,
  getTextFontColors,
  PropertiesWrapper,
  PropertyLabel,
  SectionHeading,
  StyledDivider,
} from './TopBarComponentProperties.styles'

export interface ITopBarComponentPropertiesProps {
  instance: ITopBarComponentInstance
  onChange: (updated: ITopBarComponentInstance) => void
}

export const TopBarComponentProperties: React.FC<ITopBarComponentPropertiesProps> = ({ instance, onChange }) => {
  const theme = useTheme()
  const { t: translate } = useTranslation(['dynamic-report'])

  const BACKGROUND_COLORS = getBackgroundColors(theme)
  const FONT_COLORS = getTextFontColors(theme)

  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...instance, title: e.target.value })
    },
    [instance, onChange],
  )

  const handleSubtitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...instance, subtitle: e.target.value })
    },
    [instance, onChange],
  )

  const handleBackgroundColorChange = useCallback(
    (color: string) => {
      onChange({ ...instance, backgroundColor: color })
    },
    [instance, onChange],
  )

  const handleTitleStyleChange = useCallback(
    <K extends keyof ITextStyles>(key: K, value: ITextStyles[K]) => {
      const current = instance.titleStyles ?? { ...DEFAULT_TOP_BAR_TITLE_STYLES(theme) }
      onChange({ ...instance, titleStyles: { ...current, [key]: value } })
    },
    [instance, onChange],
  )

  const handleSubtitleStyleChange = useCallback(
    <K extends keyof ITextStyles>(key: K, value: ITextStyles[K]) => {
      const current = instance.subtitleStyles ?? { ...DEFAULT_TOP_BAR_SUBTITLE_STYLES(theme) }
      onChange({ ...instance, subtitleStyles: { ...current, [key]: value } })
    },
    [instance, onChange],
  )

  const handleTitleAlignmentChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, value: TextAlignment | null) => {
      if (value) handleTitleStyleChange('alignment', value)
    },
    [handleTitleStyleChange],
  )

  const handleSubtitleAlignmentChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, value: TextAlignment | null) => {
      if (value) handleSubtitleStyleChange('alignment', value)
    },
    [handleSubtitleStyleChange],
  )

  const titleStyles = instance.titleStyles ?? DEFAULT_TOP_BAR_TITLE_STYLES(theme)
  const subtitleStyles = instance.subtitleStyles ?? DEFAULT_TOP_BAR_SUBTITLE_STYLES(theme)

  return (
    <PropertiesWrapper>
      <SectionHeading>{translate('top-bar-background', { ns: 'dynamic-report' })}</SectionHeading>

      <FieldSection>
        <ColorSwatchField
          colors={BACKGROUND_COLORS}
          selectedColor={instance.backgroundColor ?? '#1a1a2e'}
          onColorChange={handleBackgroundColorChange}
        />
      </FieldSection>

      <StyledDivider />

      <SectionHeading>{translate('top-bar-title-styles', { ns: 'dynamic-report' })}</SectionHeading>

      <FieldSection>
        <PropertyLabel>{translate('top-bar-title', { ns: 'dynamic-report' })}</PropertyLabel>
        <AppTextField
          size="small"
          fullWidth
          value={instance.title}
          onChange={handleTitleChange}
          placeholder={translate('top-bar-title-placeholder', { ns: 'dynamic-report' })}
        />
      </FieldSection>

      <FieldSection>
        <PropertyLabel>{translate('font-color', { ns: 'dynamic-report' })}</PropertyLabel>
        <ColorSwatchField
          colors={FONT_COLORS}
          selectedColor={titleStyles.fontColor}
          onColorChange={color => handleTitleStyleChange('fontColor', color)}
        />
      </FieldSection>

      <FieldSection>
        <PropertyLabel>{translate('font-size', { ns: 'dynamic-report' })}</PropertyLabel>
        <FontSizeSelect
          value={titleStyles.fontSize}
          onChange={size => handleTitleStyleChange('fontSize', size)}
          sizes={FONT_SIZES}
        />
      </FieldSection>

      <FieldSection>
        <PropertyLabel>{translate('font-family', { ns: 'dynamic-report' })}</PropertyLabel>
        <FontFamilySelect
          value={titleStyles.fontFamily}
          onChange={family => handleTitleStyleChange('fontFamily', family)}
        />
      </FieldSection>

      <FieldSection>
        <PropertyLabel>{translate('alignment', { ns: 'dynamic-report' })}</PropertyLabel>
        <AlignmentToggleGroup value={titleStyles.alignment} onChange={handleTitleAlignmentChange} />
      </FieldSection>

      <BoldToggleRow
        label={translate('bold', { ns: 'dynamic-report' })}
        checked={titleStyles.bold}
        onChange={checked => handleTitleStyleChange('bold', checked)}
      />

      <FieldSection>
        <PropertyLabel>{translate('line-height', { ns: 'dynamic-report' })}</PropertyLabel>
        <LineHeightSelect
          value={titleStyles.lineHeight ?? 1.25}
          onChange={val => handleTitleStyleChange('lineHeight', val)}
        />
      </FieldSection>

      <FieldSection>
        <PropertyLabel>{translate('letter-spacing', { ns: 'dynamic-report' })}</PropertyLabel>
        <LetterSpacingSelect
          value={titleStyles.letterSpacing ?? 0}
          onChange={val => handleTitleStyleChange('letterSpacing', val)}
        />
      </FieldSection>

      <StyledDivider />

      <SectionHeading>{translate('top-bar-subtitle-styles', { ns: 'dynamic-report' })}</SectionHeading>

      <FieldSection>
        <PropertyLabel>{translate('top-bar-subtitle', { ns: 'dynamic-report' })}</PropertyLabel>
        <AppTextField
          size="small"
          fullWidth
          value={instance.subtitle}
          onChange={handleSubtitleChange}
          placeholder={translate('top-bar-subtitle-placeholder', { ns: 'dynamic-report' })}
        />
      </FieldSection>

      <FieldSection>
        <PropertyLabel>{translate('font-color', { ns: 'dynamic-report' })}</PropertyLabel>
        <ColorSwatchField
          colors={FONT_COLORS}
          selectedColor={subtitleStyles.fontColor}
          onColorChange={color => handleSubtitleStyleChange('fontColor', color)}
        />
      </FieldSection>

      <FieldSection>
        <PropertyLabel>{translate('font-size', { ns: 'dynamic-report' })}</PropertyLabel>
        <FontSizeSelect
          value={subtitleStyles.fontSize}
          onChange={size => handleSubtitleStyleChange('fontSize', size)}
          sizes={FONT_SIZES}
        />
      </FieldSection>

      <FieldSection>
        <PropertyLabel>{translate('font-family', { ns: 'dynamic-report' })}</PropertyLabel>
        <FontFamilySelect
          value={subtitleStyles.fontFamily}
          onChange={family => handleSubtitleStyleChange('fontFamily', family)}
        />
      </FieldSection>

      <FieldSection>
        <PropertyLabel>{translate('alignment', { ns: 'dynamic-report' })}</PropertyLabel>
        <AlignmentToggleGroup value={subtitleStyles.alignment} onChange={handleSubtitleAlignmentChange} />
      </FieldSection>

      <BoldToggleRow
        label={translate('bold', { ns: 'dynamic-report' })}
        checked={subtitleStyles.bold}
        onChange={checked => handleSubtitleStyleChange('bold', checked)}
      />

      <FieldSection>
        <PropertyLabel>{translate('line-height', { ns: 'dynamic-report' })}</PropertyLabel>
        <LineHeightSelect
          value={subtitleStyles.lineHeight ?? 1.3}
          onChange={val => handleSubtitleStyleChange('lineHeight', val)}
        />
      </FieldSection>

      <FieldSection>
        <PropertyLabel>{translate('letter-spacing', { ns: 'dynamic-report' })}</PropertyLabel>
        <LetterSpacingSelect
          value={subtitleStyles.letterSpacing ?? 0}
          onChange={val => handleSubtitleStyleChange('letterSpacing', val)}
        />
      </FieldSection>
    </PropertiesWrapper>
  )
}
